'use client'

import { DewiAccordion } from '@components/Accordion'
import { Box, Fade, Typography, styled, useMediaQuery } from '@mui/material'
import Image from 'next/image'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DiamondIcon from '@assets/diamond.png'
import { Text } from '@components/Helpers'
import DewiButton from '@components/DewiButton'
import { useRouter } from 'next/navigation'
import dewiDoor from '@assets/dewiDoor2.png'
import dynamic from 'next/dynamic'
import { useAsync } from 'react-async-hook'
import { useSolana } from '@dewicats/connect-button'
import { getDeWiCatCollectables } from '@utils/solanaUtils'

const ConnectButton = dynamic(() => import('@dewicats/connect-button'), {
  ssr: false,
})

const BoldText = {
  fontWeight: 'bold',
}

const InheritColor = {
  color: 'inherit',
}

const DeWiHallTitle = styled(Typography, { label: 'DeWiHallTitle' })(
  ({ theme }: any) => ({
    [theme.breakpoints.down('md')]: {
      // Below 600px
      fontSize: '60px',
      textAlign: 'center',
    },
    fontFamily: 'Karmatic',
    fontSize: '70px',
    fontWeight: '700',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    color: '#fff',
    // make it a purple and light blue gradient with high saturation
    background: 'linear-gradient(90deg, #8A2BE2 0%, #00BFFF 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',

    textShadow: '0 0 0.02em #fff',
    filter: 'blur(0.003em)',

    // paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    // marginBottom: theme.spacing(5),
  }),
)

const RootLayout = styled(Box)(() => ({
  minHeight: '100dvh',
  maxWidth: '100dvw',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  top: 100,
}))

const Container = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('xl')]: {
    width: '90%',
  },
  [theme.breakpoints.up('xl')]: {
    width: '85%',
  },
}))

const DeWiHallEntry = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const isMobile = useMediaQuery('(max-width:600px)')
  const { anchorProvider, publicKey } = useSolana()
  const [votingPower, setVotingPower] = useState(0)
  const [loadingVotingPower, setLoadingVotingPower] = useState(false)

  useAsync(async () => {
    setLoadingVotingPower(true)
    try {
      const cats = await getDeWiCatCollectables(anchorProvider)
      setVotingPower(cats.length)
    } catch {
      setVotingPower(0)
    }
    setLoadingVotingPower(false)
  }, [anchorProvider?.publicKey?.toBase58(), publicKey?.toBase58()])

  const accordionItems = useMemo(() => {
    const accordionItemObjects: any = t('faq.dewiHallEntryItems', {
      returnObjects: true,
    })

    if (!accordionItemObjects) return []

    return Array.from(accordionItemObjects).map((item: any, index: any) => ({
      title: item.title,
      content: item.content,
      id: `panel${index + 1}`,
    }))
  }, [t])

  const onBrowseProposals = useCallback(() => {
    router.push('/dewihall/proposals')
  }, [router])

  const Overview = useCallback(() => {
    return (
      <div className="mt-12 mb-12">
        <h2 className="text-gray-1 text-size-7 font-bold font-space">
          {t('dewiHallEntry.overview')}
        </h2>
        <div className="flex flex-col gap-4 mb-12 mt-4 text-gray-5">
          <Text sx={InheritColor}>
            {t('dewiHallEntry.overviewDescription')}
          </Text>
          <Text sx={InheritColor}>{t('dewiHallEntry.overviewObjectives')}</Text>
          <ul
            className="ml-8"
            style={{
              listStyle: 'disc',
              color: 'white',
            }}
          >
            <li className="text-gray-5">
              <Text sx={InheritColor}>{t('dewiHallEntry.objective1')}</Text>
            </li>
            <li className="text-gray-5">
              <Text sx={InheritColor}>{t('dewiHallEntry.objective2')}</Text>
            </li>
            <li className="text-gray-5">
              <Text sx={InheritColor}>{t('dewiHallEntry.objective3')}</Text>
            </li>
          </ul>
          <Text sx={InheritColor}>{t('dewiHallEntry.objectiveSummary')}</Text>
        </div>
      </div>
    )
  }, [t])

  const Funding = useCallback(() => {
    return (
      <div className="mt-32 mb-12">
        <h5 className="text-gray-5 text-size-3 font-space uppercase font-bold text-center">
          {t('dewiHallEntry.fundingTitle')}
        </h5>
        <div className="flex xs:flex-col md:flex-row gap-4 mt-8">
          <div className="flex flex-col basis-full gap-2">
            <span className="text-gray-1 text-size-4 uppercase font-bold text-center">
              {t('dewiHallEntry.fundingReason1Title')}
            </span>
            <span className="text-gray-7 text-size-0 uppercase font-bold text-center">
              {t('dewiHallEntry.fundingReason1Desc')}
            </span>
          </div>
          <div className="flex flex-col basis-full gap-2">
            <span className="text-gray-1 text-size-4 uppercase font-bold text-center">
              {t('dewiHallEntry.fundingReason2Title')}
            </span>
            <span className="text-gray-7 text-size-0 uppercase font-bold text-center">
              {t('dewiHallEntry.fundingReason2Desc')}
            </span>
          </div>
          <div className="flex flex-col basis-full gap-2">
            <span className="text-gray-1 text-size-4 uppercase font-bold text-center">
              {t('dewiHallEntry.fundingReason3Title')}
            </span>
            <span className="text-gray-7 text-size-0 uppercase font-bold text-center">
              {t('dewiHallEntry.fundingReason3Desc')}
            </span>
          </div>
          <div className="flex flex-col basis-full gap-2">
            <span className="text-gray-1 text-size-4 uppercase font-bold text-center">
              {t('dewiHallEntry.fundingReason4Title')}
            </span>
            <span className="text-gray-7 text-size-0 uppercase font-bold text-center">
              {t('dewiHallEntry.fundingReason4Desc')}
            </span>
          </div>
        </div>
      </div>
    )
  }, [t])

  const MeowAmendements = useCallback(() => {
    return (
      <div className="mb-12 mt-32">
        <h2 className="text-gray-3 text-size-7 font-space font-bold text-left mb-8">
          {t('dewiHallEntry.meowAmendmentsTitle')}
        </h2>
        <div className="flex flex-col gap-8">
          <div className="flex gap-8 xs:flex-col md:flex-row">
            <div className="flex flex-col gap-3 basis-full">
              <div className="flex rounded-full bg-dark-5 w-8 h-6 items-center justify-center">
                <span className="text-gray-5 text-size-0 uppercase font-bold text-left">
                  {t('dewiHallEntry.one')}
                </span>
              </div>
              <div>
                <span className="text-gray-1 text-size-1 font-bold text-left">
                  {t('dewiHallEntry.meowAmendmentTitle')}
                </span>
                <span className="text-gray-7 text-size-1 text-left">
                  {t('dewiHallEntry.meowAmendmentDesc')}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-3 basis-full">
              <div className="flex rounded-full bg-dark-5 w-8 h-6 items-center justify-center">
                <span className="text-gray-5 text-size-0 uppercase font-bold text-left">
                  {t('dewiHallEntry.two')}
                </span>
              </div>
              <div>
                <span className="text-gray-1 text-size-1 font-bold text-left">
                  {t('dewiHallEntry.meowAmendmentTitle2')}
                </span>
                <span className="text-gray-7 text-size-1 text-left">
                  {t('dewiHallEntry.meowAmendmentDesc2')}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-3 basis-full">
              <div className="flex rounded-full bg-dark-5 w-8 h-6 items-center justify-center">
                <span className="text-gray-5 text-size-0 uppercase font-bold text-left">
                  {t('dewiHallEntry.three')}
                </span>
              </div>
              <div>
                <span className="text-gray-1 text-size-1 font-bold text-left">
                  {t('dewiHallEntry.meowAmendmentTitle3')}
                </span>
                <span className="text-gray-7 text-size-1 text-left">
                  {t('dewiHallEntry.meowAmendmentDesc3')}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-3 basis-full">
              <div className="flex rounded-full bg-dark-5 w-8 h-6 items-center justify-center">
                <span className="text-gray-5 text-size-0 uppercase font-bold text-left">
                  {t('dewiHallEntry.four')}
                </span>
              </div>
              <div>
                <span className="text-gray-1 text-size-1 font-bold text-left">
                  {t('dewiHallEntry.meowAmendmentTitle4')}
                </span>
                <span className="text-gray-7 text-size-1 text-left">
                  {t('dewiHallEntry.meowAmendmentDesc4')}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-8 xs:flex-col md:flex-row">
            <div className="flex flex-col gap-3 basis-full">
              <div className="flex rounded-full bg-dark-5 w-8 h-6 items-center justify-center">
                <span className="text-gray-5 text-size-0 uppercase font-bold text-left">
                  {t('dewiHallEntry.five')}
                </span>
              </div>
              <div>
                <span className="text-gray-1 text-size-1 font-bold text-left">
                  {t('dewiHallEntry.meowAmendmentTitle5')}
                </span>
                <span className="text-gray-7 text-size-1 text-left">
                  {t('dewiHallEntry.meowAmendmentDesc5')}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-3 basis-full">
              <div className="flex rounded-full bg-dark-5 w-8 h-6 items-center justify-center">
                <span className="text-gray-5 text-size-0 uppercase font-bold text-left">
                  {t('dewiHallEntry.six')}
                </span>
              </div>
              <div>
                <span className="text-gray-1 text-size-1 font-bold text-left">
                  {t('dewiHallEntry.meowAmendmentTitle6')}
                </span>
                <span className="text-gray-7 text-size-1 text-left">
                  {t('dewiHallEntry.meowAmendmentDesc6')}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-3 basis-full">
              <div className="flex rounded-full bg-dark-5 w-8 h-6 items-center justify-center">
                <span className="text-gray-5 text-size-0 uppercase font-bold text-left">
                  {t('dewiHallEntry.seven')}
                </span>
              </div>
              <div>
                <span className="text-gray-1 text-size-1 font-bold text-left">
                  {t('dewiHallEntry.meowAmendmentTitle7')}
                </span>
                <span className="text-gray-7 text-size-1 text-left">
                  {t('dewiHallEntry.meowAmendmentDesc7')}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-3 basis-full">
              <div className="flex rounded-full bg-dark-5 w-8 h-6 items-center justify-center">
                <span className="text-gray-5 text-size-0 uppercase font-bold text-left">
                  {t('dewiHallEntry.eight')}
                </span>
              </div>
              <div>
                <span className="text-gray-1 text-size-1 font-bold text-left">
                  {t('dewiHallEntry.meowAmendmentTitle8')}
                </span>
                <span className="text-gray-7 text-size-1 text-left">
                  {t('dewiHallEntry.meowAmendmentDesc8')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }, [t])

  const VotingPower = useCallback(() => {
    return (
      <div>
        <h2 className="text-gray-3 text-size-7 font-space font-bold text-left mb-8">
          {t('dewiHallEntry.votingPower')}
        </h2>
        <div className="bg-dark-8 rounded-lg p-4 border-dark-4 border-2 flex xs:flex-col md:flex-row">
          <div className="flex flex-col basis-full grow justify-center p-8 gap-4">
            <div className="flex flex-row items-center gap-4">
              <Image
                src={DiamondIcon}
                alt="Diamond Icon"
                width={40}
                height={40}
                style={{
                  objectFit: 'contain',
                }}
              />
              {!loadingVotingPower && (
                <h5 className="text-gray-1 text-size-6 font-space uppercase font-bold text-center">
                  {votingPower}
                </h5>
              )}
              {loadingVotingPower && <Spinner />}
            </div>
            <h5 className="text-gray-5 text-size-3 font-space text-left">
              {t('dewiHallEntry.votingPowerSubtitle')}
            </h5>
            <h5 className="text-gray-7 text-size-2 font-space text-left">
              {t('dewiHallEntry.votingPowerFuture')}
            </h5>
          </div>
          <div className="flex flex-col basis-full bg-dark-6 border-dark-4 border-2 grow p-10 rounded-lg gap-8">
            <h5 className="text-gray-5 text-size-3 font-space text-center">
              {t('dewiHallEntry.connectYourWallet')}
            </h5>
            <ConnectButton disableMagicLink />
          </div>
        </div>
      </div>
    )
  }, [votingPower, loadingVotingPower, t])

  return (
    <Fade in>
      <RootLayout>
        <Container component="section" className="mb-32">
          <div className="flex xs:flex-col md:flex-row md:mt-12">
            <div className="flex flex-col xs:p-2 md:p-12">
              {isMobile && (
                <div className="flex flex-1 xs:justify-center md:justify-end xs:mb-8 md:mb-0">
                  <Image
                    src={dewiDoor}
                    alt="Key Icon"
                    width={isMobile ? 200 : 300}
                    height={isMobile ? 200 : 300}
                    style={{
                      objectFit: 'contain',
                    }}
                  />
                </div>
              )}
              {!isMobile && (
                <DeWiHallTitle>{t('dewiHallEntry.dewiHall')}</DeWiHallTitle>
              )}
              {isMobile && (
                <>
                  <DeWiHallTitle>{t('dewiHallEntry.dewi')}</DeWiHallTitle>
                  <DeWiHallTitle>{t('dewiHallEntry.hall')}</DeWiHallTitle>
                </>
              )}

              <div className="flex flex-col md:w-[476px] gap-8 mt-8">
                <Text sx={BoldText}>{t('dewiHallEntry.subtitle')}</Text>
                <DewiButton
                  variant="outlined"
                  size="small"
                  onClick={onBrowseProposals}
                  sx={{
                    height: '50px',
                  }}
                >
                  {t('dewiHallEntry.browseProposals')}
                </DewiButton>
              </div>
            </div>
            {!isMobile && (
              <div className="flex flex-1 xs:justify-center md:justify-end xs:mb-8 md:mb-0">
                <Image
                  src={dewiDoor}
                  alt="Key Icon"
                  width={isMobile ? 200 : 300}
                  height={isMobile ? 200 : 300}
                  style={{
                    objectFit: 'contain',
                  }}
                />
              </div>
            )}
          </div>

          <Overview />
          <VotingPower />
          <Funding />
          <MeowAmendements />
          <h2 className="text-gray-3 text-size-7 font-space font-bold text-center mt-32 mb-16">
            {t('dewiHallEntry.faqTitle')}
          </h2>
          <DewiAccordion items={accordionItems} />
        </Container>
      </RootLayout>
    </Fade>
  )
}

const Spinner = () => {
  const { t } = useTranslation()
  return (
    <div role="status">
      <svg
        aria-hidden="true"
        className="w-6 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-gray-100"
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
      <span className="sr-only">{t('dewiHallEntry.loading')}</span>
    </div>
  )
}

export default DeWiHallEntry
